import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "hpsfir-card order" }

import {
  HpsfirProTable,
  HpsfirProTableColumn,
  HpsfirProTableData,
  HpsfirProTableFilter,
  HpsfirProTableGetDataFunction,
  useBaseAsync,
} from 'hpsfir-managebase-ui';
import { useApi } from '@/hooks/useApi';
import { ref } from 'vue';
import dayjs from 'dayjs';
import Decimal from 'decimal.js';
import { getGoodsDisplayName } from '@/utils/goodsSpecs';


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const formatMoney = (value?: string | number) => new Decimal(value || 0).toFixed(2);

const formatPoints = (value?: string | number) => new Decimal(value || 0).toString();

const hasMoneyAmount = (value?: string | number) => new Decimal(value || 0).gt(0);

const hasPointsAmount = (value?: string | number) => new Decimal(value || 0).gt(0);

const isPointsGoods = (item: any) => Boolean(item?.isPointsExchange) || hasPointsAmount(item?.totalPoints || item?.pointsPrice);

const getGoodsMoneyTotal = (item: any) => item.total
  || new Decimal(item.discountPrice || item.price || 0)
    .mul(item.count || 1)
    .toFixed(2);

const getGoodsPointsTotal = (item: any) => item.totalPoints
  || new Decimal(item.pointsPrice || item.price || 0)
    .mul(item.count || 1)
    .toString();

const getFullUrl = (url: string) => {
  if (!url) {
    return '';
  }
  if (url.startsWith('http')) {
    return url;
  }
  return useApi().baseUrl + url;
};

const columns = ref<HpsfirProTableColumn[]>([]);
const tableRef = ref<InstanceType<typeof HpsfirProTable>>();

const conditionsInitValue = {};

// eslint-disable-next-line max-lines-per-function
const createColumns = () => [
  {
    name: 'id',
    label: '订单号',
    type: 'string',
    width: 180,
  },
  {
    name: 'seatInfo',
    label: '桌位',
    type: 'string',
    width: 100,
  },
  {
    name: 'goods',
    label: '商品',
    type: 'string',
    minWidth: 200,
    render(scope: any) {
      const { goods } = scope.row;
      if (!goods || goods.length === 0) {
        return <span>-</span>;
      }
      return (
        <div class="goods-cell">
          {goods.map((item: any, idx: number) => (
            <div key={idx} class="goods-item">
              {getGoodsDisplayName(item)} x{item.count || 1} · {isPointsGoods(item)
                ? `${formatPoints(getGoodsPointsTotal(item))}积分`
                : `¥ ${formatMoney(getGoodsMoneyTotal(item))}`}
            </div>
          ))}
        </div>
      );
    },
  },
  {
    name: 'price',
    label: '金额',
    type: 'string',
    width: 100,
    render(scope: any) {
      const { price, pointsPrice, status } = scope.row;
      const isCancelled = status === 'cancelled';
      return (
        <div class={isCancelled ? 'price-text price-cancelled' : 'price-text'}>
          {hasMoneyAmount(price) && <div>¥ {formatMoney(price)}</div>}
          {hasPointsAmount(pointsPrice) && <div>{formatPoints(pointsPrice)} 积分</div>}
          {!hasMoneyAmount(price) && !hasPointsAmount(pointsPrice) && <div>¥ 0.00</div>}
        </div>
      );
    },
  },
  {
    name: 'userId',
    label: '用户',
    type: 'remoteSearchSelect',
    fetchOptions: async(query) => {
      const res = await useApi().userApi.pageUsersuser({
        conditions: {
          meta: `phone": "%${query}%"` as any,
        },
        pageQuery: {
          page: 0,
          pageSize: 10,
        },
      });
      return res.data.data?.data?.map((item: any) => ({
        label: `${item.meta.name}(${item.meta.phone})`,
        value: item.id,
      })) || [];
    },
    placeholder: '搜索手机号',
    isCondition: true,
    hideTable: true,
    width: 120,
  },
  {
    name: 'payType',
    label: '支付方式',
    type: 'select',
    options: [
      { label: '微信支付', value: 'wechat' },
      { label: '现金支付', value: 'cash' },
      { label: 'D币支付', value: 'coins' },
      { label: '积分支付', value: 'points' },
      { label: '券兑换', value: 'coupon' },
      { label: '其他', value: 'other' },
    ],
    clearable: true,
    isCondition: true,
    width: 100,
    render(scope: any) {
      const { payType } = scope.row;
      const payTypeMap: Record<string, string> = {
        wechat: '微信支付',
        cash: '现金支付',
        coins: 'D币支付',
        points: '积分支付',
        coupon: '券兑换',
        other: '其他',
      };
      return <span>{payTypeMap[payType] || payType || '-'}</span>;
    },
  },


  {
    name: 'userInfo',
    label: '用户',
    type: 'string',
    width: 200,
    render(scope: any) {
      const { userInfo } = scope.row;
      if (!userInfo) {
        return <span>-</span>;
      }
      const { avatar, name, phone } = userInfo;
      return (
        <div class="user-info-cell">
          <el-avatar size={40} src={avatar ? getFullUrl(avatar) : ''}>
            {name ? name.charAt(0) : '?'}
          </el-avatar>
          <div class="user-detail">
            <div class="user-name">{name || '-'}</div>
            <div class="user-phone">{phone || '-'}</div>
          </div>
        </div>
      );
    },
  },
  {
    name: 'status',
    label: '状态',
    type: 'select',
    options: [
      { label: '待出餐', value: 'waiting' },
      { label: '已完成', value: 'completed' },
      { label: '已取消', value: 'cancelled' },
    ],
    clearable: true,
    isCondition: true,
    width: 100,
    render(scope: any) {
      const { status } = scope.row;
      if (status === 'waiting') {
        return <el-tag type="warning">待出餐</el-tag>;
      }
      if (status === 'completed') {
        return <el-tag type="success">已完成</el-tag>;
      }
      if (status === 'cancelled') {
        return <el-tag type="info">已取消</el-tag>;
      }
      if (status === 'unpaid') {
        return <el-tag type="danger">待支付</el-tag>;
      }
      return <span>{status || '-'}</span>;
    },
  },
  {
    name: 'timeRange',
    label: '时间',
    type: 'datepick',
    isCondition: true,
    hideTable: true,
    clearable: true,
    pickType: 'datetimerange',
    defaultTime: [
      dayjs().startOf('day')
        .toDate(),
      dayjs().endOf('day')
        .toDate(),
    ],
    width: 180,
  },
  {
    name: 'createTime',
    label: '时间',
    type: 'string',
    width: 180,
  },
] as HpsfirProTableColumn[];

const init = () => {
  columns.value = createColumns();
};

const operates: { name: string; label: string; type: string }[] = [];

const loading = ref(false);

const getData = useBaseAsync<HpsfirProTableGetDataFunction>(async(filter: HpsfirProTableFilter) => {
  try {
    loading.value = true;
    const res = await useApi().billApi.pageBillbill({
      conditions: {
        billType: 'order',
        ...Object.entries(filter.conditions).reduce((conditions, [key, val]) => {
          if (val === null || val === undefined || val === '') {
            return conditions;
          }
          conditions[key as keyof typeof conditions] = val;
          return conditions;
        }, {} as Record<string, unknown>),
      },
      pageQuery: {
        page: filter.paginations.page - 1,
        pageSize: filter.paginations.pageSize,
      },
      timeRange: filter.conditions.timeRange && {
        start: filter.conditions.timeRange[0],
        end: filter.conditions.timeRange[1],
        field: 'createTime',
      },
    });

    const tableData: HpsfirProTableData = {
      total: res.data.data?.total || 0,
      data: res.data.data?.data || [],
    };
    return tableData;
  } finally {
    loading.value = false;
  }
});

init();

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(HpsfirProTable), {
      ref_key: "tableRef",
      ref: tableRef,
      paginations: { pageSize: 20 },
      "page-layout": "total, sizes, prev, pager, next",
      columns: columns.value,
      "get-data": _unref(getData),
      loading: loading.value,
      "row-key": "id",
      operates: operates,
      "codition-init-values": conditionsInitValue,
      "hide-create": true,
      "hide-delete": true,
      "hide-edit": true
    }, null, 8, ["columns", "get-data", "loading"])
  ]))
}
}

})